import { ValueGetterParams } from 'ag-grid-community';
import { Maturity, BasicMaturity } from '../../../types/deal.types';
import { blackoutValueGetter } from './maturity-getter';
import { MaturityAnalyticsColumnName } from '../../types/column.types';
import { FilterResultFacade } from '../../../helpers/filters/filter-result-facade';
import { variousFormatter } from '../formatters/various';
import { DealMaturities } from '../../../helpers/issue/maturity';
import { formatNumber } from '@angular/common';
import { ComplexValue } from '../../../types/complex-value';
import { CalendarGridViewModel } from '../../../types/deal.viewmodel.types';
import { formatNumberWithLeadingSign } from '../../../utils/formatters/leading-sign';
import { BenchmarkCurve } from '../../../types/benchmark.types';
import { getBenchmarkDataPointByMaturityDate } from '../../../helpers/benchmark-curve';

export function getPricingValueGetter(field: MaturityAnalyticsColumnName) {
  return function (params: ValueGetterParams): ComplexValue {
    const { data, context } = params;

    const filterResult: FilterResultFacade =
      context && context.getFilterResult();
    const dealResult = filterResult.getDealFilterResult(data.id);
    const filteredMaturities: Maturity[] = data.maturities.filter((mat) => {
      const entity = dealResult.maturities[mat.id];
      return entity && entity.filterResult;
    });

    const primaryValues = getPricingPrimaryValue(
      filteredMaturities,
      data,
      field
    );
    const additionalValues = new Set<string>();

    for (const maturity of filteredMaturities) {
      const maturitySeries = data.series.find(
        (s) => s.id === maturity.seriesId
      );
      const isSeriesTaxable = maturitySeries.isTaxable;
      const matAdditionalValue = isSeriesTaxable
        ? getAdditionalTaxablePricingValue(maturity, field)
        : getAdditionalNotTaxablePricingValue(
            maturity,
            field,
            context.getLocale()
          );
      if (matAdditionalValue !== null && matAdditionalValue !== undefined) {
        additionalValues.add(matAdditionalValue);
      }
    }

    return {
      primaryValue: primaryValues,
      additionalValue: variousFormatter()({
        value: Array.from(additionalValues)
      })
    };
  };
}

export function getPricingPrimaryValue(
  filteredMaturities: Maturity[],
  deal: CalendarGridViewModel,
  field: MaturityAnalyticsColumnName
) {
  const primaryValues = new Set<string | number>();
  for (const maturity of filteredMaturities) {
    let matPrimaryValue: number | string;
    if (field === MaturityAnalyticsColumnName.Spread) {
      matPrimaryValue = getSpreadValue(maturity, deal);
    } else {
      matPrimaryValue = blackoutValueGetter(field)({
        data: maturity
      } as ValueGetterParams);
    }
    if (matPrimaryValue !== null && matPrimaryValue !== undefined) {
      primaryValues.add(matPrimaryValue);
    }
  }

  return Array.from(primaryValues);
}

export function getSpreadValue(
  maturity: BasicMaturity,
  deal: CalendarGridViewModel
): string | number {
  const maturitySeries = deal.series.find((s) => s.id === maturity.seriesId);
  if (maturitySeries.isTaxable) {
    return getTaxableSpread(maturity);
  }
  const benchmarkCurve = deal.benchmarkCurve;
  return getNotTaxableSpread(maturity, benchmarkCurve);
}

export function getTaxableSpread(maturity: BasicMaturity): string {
  const result =
    maturity['taxableTreasurySpread'] || maturity['taxableTsySpread'];
  const withSign = formatNumberWithLeadingSign(Math.round(result), 'en-US', {
    int: '1.0-0',
    float: '1.0-0'
  });
  return result ? `UST ${withSign}` : null;
}

export function getNotTaxableSpread(
  maturity: BasicMaturity,
  benchmarkCurve: BenchmarkCurve
): number {
  if (maturity.pacBond) {
    return null;
  }

  const benchmarkPoint =
    benchmarkCurve &&
    getBenchmarkDataPointByMaturityDate(
      maturity.chartDate,
      benchmarkCurve.data
    );

  return maturity.yield && benchmarkPoint
    ? Math.round((maturity.yield - (benchmarkPoint.yield as number)) * 100)
    : null;
}

export function getAdditionalNotTaxablePricingValue(
  maturity: BasicMaturity,
  column: MaturityAnalyticsColumnName,
  locale: string
) {
  if (column === MaturityAnalyticsColumnName.Coupon) {
    return null;
  }
  return formatNumber(
    blackoutValueGetter('coupon')({
      data: maturity
    } as ValueGetterParams),
    locale,
    '1.3'
  );
}

export function getAdditionalTaxablePricingValue(
  maturity: BasicMaturity,
  column: MaturityAnalyticsColumnName
) {
  if (column !== MaturityAnalyticsColumnName.Spread) {
    return null;
  }
  return DealMaturities.getTreasuryYear(maturity);
}
